import {
  clearAdsAttributionCookie,
  getAdsAttributionCookie,
  setAdsAttributionCookie,
} from "./adsAttributionCookie";
import {
  getGoogleAdsClickId,
  GOOGLE_ADS_CLICK_ID_COOKIE_MAX_AGE_DAYS,
  GOOGLE_ADS_CLICK_ID_QUERY_PARAMS,
  GOOGLE_ADS_CLICK_ID_STORAGE_KEYS,
  removeGoogleAdsClickIdsFromSearchParams,
} from "./googleAdsClickId";

import type { GoogleAdsClickId } from "./googleAdsClickId";

const GOOGLE_TAG_SCRIPT_ID = "google-ads-tag";
const GOOGLE_TAG_SCRIPT_URL = "https://www.googletagmanager.com/gtag/js";
const GOOGLE_ADS_MEASUREMENT_READY_TIMEOUT_MS = 1500;
const GOOGLE_TAG_SCRIPT_LOAD_TIMEOUT_MS = 1500;
const GOOGLE_ADS_DELIVERY_TIMEOUT_MS = 1000;
const GOOGLE_ADS_REQUEST_PATH_SEGMENTS = [
  "/ccm/",
  "/measurement/",
  "/pagead/",
  "/rmkt/",
  "/td/",
  "/travel/",
];
const GOOGLE_ADS_REQUEST_HOSTS = new Set([
  "ad.doubleclick.net",
  "adservice.google.com",
  "google.com",
  "googleads.g.doubleclick.net",
  "pagead2.googlesyndication.com",
  "td.doubleclick.net",
  "www.google.com",
  "www.googleadservices.com",
  "www.googletagmanager.com",
]);
const GOOGLE_ADS_REQUEST_SANITIZATION_INSTALLED = "__tapestryGoogleAdsRequestSanitizationInstalled";
const RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT = {
  page_location: "https://ads.openai.com/",
  page_referrer: "",
  page_title: "OpenAI Ads",
  ignore_referrer: true,
} as const;
const GOOGLE_ADS_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS = [
  ["url", RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_location],
  ["ref", RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_referrer],
  ["top", "redacted"],
  ["tiba", RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_title],
] as const;

type Gtag = (...args: unknown[]) => void;
type GoogleAdsSanitizationWindow = Window & {
  [GOOGLE_ADS_REQUEST_SANITIZATION_INSTALLED]?: boolean;
};
type GoogleAdsRequestSrcElement = HTMLIFrameElement | HTMLImageElement | HTMLScriptElement;
type GoogleAdsRequestSrcElementConstructor = {
  prototype: GoogleAdsRequestSrcElement;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let measurementEnabled = false;
let initializedTagId: string | undefined;
let accountCreationConversionLabel: string | undefined;
let googleAdsConsentResolved = true;
let shareAllConversions = false;
let measurementReadyWaiters: Array<(ready: boolean) => void> = [];
let googleTagScriptLoaded = false;
let googleTagScriptLoadPromise: Promise<boolean> | undefined;

const GOOGLE_ADS_CONFIG = {
  send_page_view: false,
  // Initialization is gated on resolved marketing consent, so the linker may
  // persist the landing-page click ID only after the user permits marketing.
  conversion_linker: true,
  // Keep Google-managed attribution cookies on the Ads host instead of .openai.com.
  cookie_domain: "none",
  allow_ad_personalization_signals: false,
  restricted_data_processing: true,
} as const;

export function setGoogleAdsMeasurementEnabled(
  enabled: boolean,
  {
    consentResolved = true,
    shouldClearAttribution = false,
    shouldShareAllConversions = false,
  }: {
    consentResolved?: boolean;
    shouldClearAttribution?: boolean;
    shouldShareAllConversions?: boolean;
  } = {},
) {
  measurementEnabled = enabled;
  googleAdsConsentResolved = consentResolved;
  shareAllConversions = shouldShareAllConversions;
  if (typeof window === "undefined") {
    notifyMeasurementStateChanged();
    return;
  }

  if (shouldClearAttribution) {
    clearStoredGoogleAdsClickId();
    removeGoogleAdsClickIdFromCurrentUrl();
  }

  if (!window.gtag) {
    notifyMeasurementStateChanged();
    return;
  }

  callGtag(
    window.gtag,
    "Failed to update Google Ads consent",
    "consent",
    "update",
    consentSettings(enabled),
  );
  notifyMeasurementStateChanged();
}

export function initializeGoogleAdsMeasurement({
  tagId,
  accountCreationLabel,
}: {
  tagId: string;
  accountCreationLabel: string;
}) {
  if (!measurementEnabled || !tagId || !accountCreationLabel || typeof window === "undefined") {
    return;
  }

  captureGoogleAdsClickIdFromCurrentUrl();

  try {
    installGoogleAdsRequestSanitization();
    const gtag = getOrCreateGtag();
    accountCreationConversionLabel = accountCreationLabel;

    if (initializedTagId !== tagId) {
      // Set the restrictive defaults before any Google config or event command.
      if (
        !callGtag(
          gtag,
          "Failed to initialize Google Ads measurement",
          "consent",
          "default",
          consentSettings(false),
        ) ||
        !callGtag(
          gtag,
          "Failed to initialize Google Ads measurement",
          "consent",
          "update",
          consentSettings(true),
        ) ||
        !callGtag(gtag, "Failed to initialize Google Ads measurement", "js", new Date()) ||
        !setGoogleAdsClickId(
          gtag,
          getStoredGoogleAdsClickId(),
          "Failed to initialize Google Ads measurement",
        ) ||
        !configureGoogleAdsTag(gtag, tagId, "Failed to initialize Google Ads measurement")
      ) {
        return;
      }
      initializedTagId = tagId;
    }
    loadGoogleTagScript(tagId);
    notifyMeasurementStateChanged();
  } catch (error) {
    console.warn("Failed to initialize Google Ads measurement", { error });
  }
}

export async function trackGoogleAdsAccountCreated(transactionId: string): Promise<void> {
  await waitForGoogleAdsMeasurementReady();

  if (
    !measurementEnabled ||
    typeof window === "undefined" ||
    !window.gtag ||
    !initializedTagId ||
    !accountCreationConversionLabel
  ) {
    return;
  }

  const googleAdsClickId = getStoredGoogleAdsClickId();
  if (!googleAdsClickId && !shareAllConversions) {
    return;
  }

  await waitForGoogleTagScript();

  if (
    !measurementEnabled ||
    (!googleAdsClickId && !shareAllConversions) ||
    !window.gtag ||
    !initializedTagId ||
    !accountCreationConversionLabel
  ) {
    return;
  }

  const gtag = window.gtag;
  const tagId = initializedTagId;
  const sendTo = tagId + "/" + accountCreationConversionLabel;
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve();
    };
    const timeoutId = window.setTimeout(finish, GOOGLE_ADS_DELIVERY_TIMEOUT_MS);
    if (
      !configureGoogleAdsTag(
        gtag,
        tagId,
        "Failed to configure Google Ads account creation conversion",
      ) ||
      !setGoogleAdsClickId(gtag, googleAdsClickId, "Failed to configure Google Ads conversion")
    ) {
      finish();
      return;
    }
    const queued = callGtag(
      gtag,
      "Failed to queue Google Ads account creation conversion",
      "event",
      "conversion",
      {
        send_to: sendTo,
        transaction_id: transactionId,
        ...(googleAdsClickId ? { [googleAdsClickId.type]: googleAdsClickId.value } : {}),
        event_callback: finish,
        event_timeout: GOOGLE_ADS_DELIVERY_TIMEOUT_MS,
      },
    );
    if (!queued) finish();
  });
}

export function captureGoogleAdsClickIdFromCurrentUrl() {
  const searchParams = new URLSearchParams(window.location.search);
  if (!GOOGLE_ADS_CLICK_ID_QUERY_PARAMS.some((queryParam) => searchParams.has(queryParam))) {
    return;
  }

  const clickId = getGoogleAdsClickId(searchParams);
  clearStoredGoogleAdsClickId();
  try {
    if (clickId) {
      window.sessionStorage.setItem(GOOGLE_ADS_CLICK_ID_STORAGE_KEYS[clickId.type], clickId.value);
    }
  } catch {
    // Ignore storage failures; attribution must not block measurement setup.
  }
  if (clickId) setGoogleAdsAttributionCookie(clickId);
  removeGoogleAdsClickIdFromCurrentUrl();
}

function getStoredGoogleAdsClickId(): GoogleAdsClickId | undefined {
  for (const type of GOOGLE_ADS_CLICK_ID_QUERY_PARAMS) {
    try {
      const storedClickId = window.sessionStorage.getItem(GOOGLE_ADS_CLICK_ID_STORAGE_KEYS[type]);
      if (storedClickId) {
        const validatedClickId = getGoogleAdsClickId(new URLSearchParams([[type, storedClickId]]));
        if (validatedClickId) return validatedClickId;
      }
    } catch {
      // Fall back to the first-party attribution cookie.
    }

    const cookieClickId = getAdsAttributionCookie(type);
    if (cookieClickId) {
      const validatedClickId = getGoogleAdsClickId(new URLSearchParams([[type, cookieClickId]]));
      if (validatedClickId) return validatedClickId;
    }
  }
  return undefined;
}

function clearStoredGoogleAdsClickId() {
  try {
    for (const storageKey of Object.values(GOOGLE_ADS_CLICK_ID_STORAGE_KEYS)) {
      window.sessionStorage.removeItem(storageKey);
    }
  } catch {
    // Consent revocation must not fail when storage is unavailable.
  }
  for (const type of GOOGLE_ADS_CLICK_ID_QUERY_PARAMS) {
    clearAdsAttributionCookie(type);
  }
}

function setGoogleAdsAttributionCookie(clickId: GoogleAdsClickId) {
  setAdsAttributionCookie({
    name: clickId.type,
    value: clickId.value,
    maxAgeDays: GOOGLE_ADS_CLICK_ID_COOKIE_MAX_AGE_DAYS,
  });
}

function setGoogleAdsClickId(
  gtag: Gtag,
  clickId: GoogleAdsClickId | undefined,
  warningMessage: string,
): boolean {
  return callGtag(gtag, warningMessage, "set", {
    gclid: null,
    gbraid: null,
    ...(clickId ? { [clickId.type]: clickId.value } : {}),
  });
}

function removeGoogleAdsClickIdFromCurrentUrl() {
  try {
    const url = new URL(window.location.href);
    if (!GOOGLE_ADS_CLICK_ID_QUERY_PARAMS.some((queryParam) => url.searchParams.has(queryParam))) {
      return;
    }

    removeGoogleAdsClickIdsFromSearchParams(url.searchParams);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // Ignore history failures; URL cleanup should not affect conversion tracking.
  }
}

function getOrCreateGtag(): Gtag {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    window.gtag = function gtagShim() {
      // Match Google's official gtag bootstrap shape so gtag.js can process queued commands.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    } as Gtag;
  }

  return window.gtag;
}

function loadGoogleTagScript(tagId: string) {
  if (googleTagScriptLoadPromise) return googleTagScriptLoadPromise;
  if (googleTagScriptLoaded) return Promise.resolve(true);

  const existingScript = document.getElementById(GOOGLE_TAG_SCRIPT_ID);
  if (existingScript instanceof HTMLScriptElement) {
    googleTagScriptLoadPromise = waitForScriptLoad(existingScript);
    return googleTagScriptLoadPromise;
  }

  const script = document.createElement("script");
  script.id = GOOGLE_TAG_SCRIPT_ID;
  script.async = true;
  script.src = GOOGLE_TAG_SCRIPT_URL + "?id=" + encodeURIComponent(tagId);
  const scriptLoadPromise = waitForScriptLoad(script);
  document.head.appendChild(script);
  googleTagScriptLoadPromise = scriptLoadPromise;
  return googleTagScriptLoadPromise;
}

function waitForGoogleTagScript(): Promise<boolean> {
  if (googleTagScriptLoaded) return Promise.resolve(true);
  return googleTagScriptLoadPromise ?? Promise.resolve(false);
}

function isGoogleAdsMeasurementReady(): boolean {
  return (
    measurementEnabled &&
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    initializedTagId != null &&
    accountCreationConversionLabel != null
  );
}

function shouldStopWaitingForMeasurementReady(): boolean {
  return googleAdsConsentResolved && !measurementEnabled;
}

function waitForGoogleAdsMeasurementReady(): Promise<boolean> {
  if (isGoogleAdsMeasurementReady()) return Promise.resolve(true);
  if (typeof window === "undefined" || shouldStopWaitingForMeasurementReady()) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      measurementReadyWaiters = measurementReadyWaiters.filter((waiter) => waiter !== finish);
      resolve(ready);
    };
    const timeoutId = window.setTimeout(
      () => finish(false),
      GOOGLE_ADS_MEASUREMENT_READY_TIMEOUT_MS,
    );
    measurementReadyWaiters.push(finish);
  });
}

function notifyMeasurementStateChanged() {
  if (!isGoogleAdsMeasurementReady() && !shouldStopWaitingForMeasurementReady()) {
    return;
  }

  const ready = isGoogleAdsMeasurementReady();
  const waiters = measurementReadyWaiters;
  measurementReadyWaiters = [];
  for (const waiter of waiters) {
    waiter(ready);
  }
}

function waitForScriptLoad(script: HTMLScriptElement): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (loaded: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      googleTagScriptLoaded = loaded;
      resolve(loaded);
    };
    const timeoutId = window.setTimeout(() => finish(false), GOOGLE_TAG_SCRIPT_LOAD_TIMEOUT_MS);
    script.addEventListener(
      "load",
      () => {
        googleTagScriptLoaded = true;
        finish(true);
      },
      { once: true },
    );
    script.addEventListener("error", () => finish(false), { once: true });
  });
}

function consentSettings(marketingConsentEnabled: boolean) {
  return {
    ad_storage: marketingConsentEnabled ? "granted" : "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  };
}

function setRestrictedGoogleAdsPageContext(gtag: Gtag): boolean {
  return (
    callGtag(
      gtag,
      "Failed to restrict Google Ads page context",
      "set",
      "page_location",
      RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_location,
    ) &&
    callGtag(
      gtag,
      "Failed to restrict Google Ads page context",
      "set",
      "page_referrer",
      RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_referrer,
    ) &&
    callGtag(
      gtag,
      "Failed to restrict Google Ads page context",
      "set",
      "page_title",
      RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_title,
    ) &&
    callGtag(
      gtag,
      "Failed to restrict Google Ads page context",
      "set",
      "ignore_referrer",
      RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.ignore_referrer,
    )
  );
}

function configureGoogleAdsTag(gtag: Gtag, tagId: string, warningMessage: string): boolean {
  return (
    setRestrictedGoogleAdsPageContext(gtag) &&
    callGtag(gtag, warningMessage, "config", tagId, GOOGLE_ADS_CONFIG)
  );
}

export function rewriteGoogleAdsRequestUrl(rawUrl: string): string;
export function rewriteGoogleAdsRequestUrl(rawUrl: URL): URL;
export function rewriteGoogleAdsRequestUrl(rawUrl: string | URL): string | URL;
export function rewriteGoogleAdsRequestUrl(rawUrl: string | URL) {
  try {
    const rawUrlString = rawUrl.toString();
    if (!couldBeGoogleAdsRequest(rawUrlString)) return rawUrl;

    const url = new URL(rawUrlString, window.location.href);
    if (!shouldSanitizeGoogleAdsRequestUrl(url)) return rawUrl;

    for (const [queryParam, value] of GOOGLE_ADS_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS) {
      if (url.searchParams.has(queryParam)) url.searchParams.set(queryParam, value);
    }
    return rawUrl instanceof URL ? url : url.toString();
  } catch {
    return rawUrl;
  }
}

function couldBeGoogleAdsRequest(rawUrl: string) {
  return (
    GOOGLE_ADS_REQUEST_PATH_SEGMENTS.some((segment) => rawUrl.includes(segment)) &&
    GOOGLE_ADS_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS.some(([param]) => rawUrl.includes(param + "="))
  );
}

function shouldSanitizeGoogleAdsRequestUrl(url: URL) {
  return (
    isGoogleAdsRequestUrl(url) &&
    GOOGLE_ADS_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS.some(([param]) => url.searchParams.has(param))
  );
}

function isGoogleAdsRequestUrl(url: URL) {
  return (
    GOOGLE_ADS_REQUEST_HOSTS.has(url.hostname) &&
    GOOGLE_ADS_REQUEST_PATH_SEGMENTS.some((segment) => url.pathname.includes(segment))
  );
}

function rewriteFetchInput(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input === "string" || input instanceof URL) return rewriteGoogleAdsRequestUrl(input);
  if (typeof Request !== "undefined" && input instanceof Request) {
    const rewrittenUrl = rewriteGoogleAdsRequestUrl(input.url);
    return rewrittenUrl === input.url ? input : new Request(rewrittenUrl, input);
  }
  return input;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object";
}

function rewriteGoogleAdsServiceWorkerCommand(command: unknown) {
  if (!isRecord(command) || !isRecord(command.params)) return command;
  const requestUrl = command.params.url;
  if (typeof requestUrl !== "string") return command;

  let url: URL;
  try {
    url = new URL(requestUrl, window.location.href);
  } catch {
    return command;
  }
  if (!isGoogleAdsRequestUrl(url)) return command;

  const rewrittenParams: Record<string, unknown> = {
    ...command.params,
    url: rewriteGoogleAdsRequestUrl(requestUrl),
  };
  if ("referer" in command.params) {
    rewrittenParams.referer = RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_location;
  }
  if ("soReferrer" in command.params) {
    rewrittenParams.soReferrer = RESTRICTED_GOOGLE_ADS_PAGE_CONTEXT.page_location;
  }
  return { ...command, params: rewrittenParams };
}

function rewriteGoogleAdsServiceWorkerMessage(message: unknown) {
  if (!isRecord(message)) return message;
  if ("command" in message) {
    const rewrittenCommand = rewriteGoogleAdsServiceWorkerCommand(message.command);
    return rewrittenCommand === message.command ?
        message
      : { ...message, command: rewrittenCommand };
  }
  return rewriteGoogleAdsServiceWorkerCommand(message);
}

function installGoogleAdsServiceWorkerRequestSanitization() {
  if (typeof ServiceWorker === "undefined") return;
  const originalPostMessage = ServiceWorker.prototype.postMessage;
  ServiceWorker.prototype.postMessage = function (
    this: ServiceWorker,
    message: unknown,
    transferOrOptions?: StructuredSerializeOptions | Transferable[],
  ) {
    return Reflect.apply(originalPostMessage, this, [
      rewriteGoogleAdsServiceWorkerMessage(message),
      transferOrOptions,
    ]);
  } as ServiceWorker["postMessage"];
}

function installGoogleAdsRequestSrcSanitization(
  elementConstructor: GoogleAdsRequestSrcElementConstructor,
) {
  const srcDescriptor = Object.getOwnPropertyDescriptor(elementConstructor.prototype, "src");
  if (!srcDescriptor?.set) return;
  Object.defineProperty(elementConstructor.prototype, "src", {
    ...srcDescriptor,
    set(this: GoogleAdsRequestSrcElement, value: string) {
      srcDescriptor.set?.call(this, rewriteGoogleAdsRequestUrl(value));
    },
  });
}

function installGoogleAdsRequestSanitization() {
  if (typeof window === "undefined") return;
  const sanitizationWindow: GoogleAdsSanitizationWindow = window;
  if (sanitizationWindow[GOOGLE_ADS_REQUEST_SANITIZATION_INSTALLED]) return;
  sanitizationWindow[GOOGLE_ADS_REQUEST_SANITIZATION_INSTALLED] = true;

  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init) => originalFetch(rewriteFetchInput(input), init);
  }
  if (typeof navigator.sendBeacon === "function") {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) => originalSendBeacon(rewriteGoogleAdsRequestUrl(url), data);
  }
  if (typeof XMLHttpRequest !== "undefined") {
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      return Reflect.apply(originalOpen, this, [method, rewriteGoogleAdsRequestUrl(url), ...rest]);
    } as XMLHttpRequest["open"];
  }
  installGoogleAdsServiceWorkerRequestSanitization();
  if (typeof HTMLIFrameElement !== "undefined") {
    installGoogleAdsRequestSrcSanitization(HTMLIFrameElement);
  }
  if (typeof HTMLImageElement !== "undefined") {
    installGoogleAdsRequestSrcSanitization(HTMLImageElement);
  }
  if (typeof HTMLScriptElement !== "undefined") {
    installGoogleAdsRequestSrcSanitization(HTMLScriptElement);
  }
}

function callGtag(gtag: Gtag, warningMessage: string, ...args: unknown[]): boolean {
  try {
    gtag(...args);
    return true;
  } catch (error) {
    console.warn(warningMessage, { error });
    return false;
  }
}
